/**
 * LogsView Component
 * 
 * Displays system logs from the Parse Server application.
 * Includes filtering by type, level, and time range.
 */

import { useState, useMemo } from 'react';
import { LogEntry, LogLevel, LogType, LogFilters } from '../../../types/AppPanel';

interface LogsViewProps {
  /** List of log entries */
  logs?: LogEntry[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** URL to open full logs page */
  dashboardUrl?: string;
  /** Whether to show live updates indicator */
  isLive?: boolean;
}

/**
 * Maps log levels to visual styles
 */
const LEVEL_STYLES: Record<LogLevel, { bg: string; text: string; label: string }> = {
  'info': { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'INFO' },
  'warn': { bg: 'bg-amber-500/15', text: 'text-amber-400', label: 'WARN' },
  'error': { bg: 'bg-red-500/15', text: 'text-red-400', label: 'ERROR' },
  'debug': { bg: 'bg-gray-500/15', text: 'text-gray-400', label: 'DEBUG' },
};

/**
 * Formats a timestamp for display
 */
function formatLogTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export default function LogsView({
  logs,
  isLoading,
  dashboardUrl,
  isLive,
}: LogsViewProps) {
  // Filter state
  const [filters, setFilters] = useState<LogFilters>({
    type: null,
    level: null,
    timeRange: '1h',
    isLive: true,
  });

  // Filter logs based on current filters
  const filteredLogs = useMemo(() => {
    if (!logs) return [];
    
    return logs.filter(log => {
      if (filters.type && log.type !== filters.type) return false;
      if (filters.level && log.level !== filters.level) return false;
      return true;
    });
  }, [logs, filters]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex items-center gap-3 text-white/50">
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span>Loading logs...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 px-4 py-3 bg-black/20 border-b border-white/5">
        <div className="flex items-center gap-3">
          {/* Type Filter */}
          <select
            value={filters.type || ''}
            onChange={(e) => setFilters(f => ({ ...f, type: e.target.value as LogType || null }))}
            className="
              appearance-none bg-white/5 border border-white/10 rounded-lg
              px-3 py-1.5 pr-8 text-xs text-white/80
              hover:bg-white/8 focus:outline-none focus:ring-2 focus:ring-[#208AEC]/40
              cursor-pointer
            "
          >
            <option value="" className="bg-[#0c121c]">All Types</option>
            <option value="api" className="bg-[#0c121c]">API</option>
            <option value="cloud-function" className="bg-[#0c121c]">Cloud Function</option>
            <option value="trigger" className="bg-[#0c121c]">Trigger</option>
            <option value="system" className="bg-[#0c121c]">System</option>
            <option value="auth" className="bg-[#0c121c]">Auth</option>
          </select>

          {/* Level Filter */}
          <select
            value={filters.level || ''}
            onChange={(e) => setFilters(f => ({ ...f, level: e.target.value as LogLevel || null }))}
            className="
              appearance-none bg-white/5 border border-white/10 rounded-lg
              px-3 py-1.5 pr-8 text-xs text-white/80
              hover:bg-white/8 focus:outline-none focus:ring-2 focus:ring-[#208AEC]/40
              cursor-pointer
            "
          >
            <option value="" className="bg-[#0c121c]">All Levels</option>
            <option value="info" className="bg-[#0c121c]">Info</option>
            <option value="warn" className="bg-[#0c121c]">Warning</option>
            <option value="error" className="bg-[#0c121c]">Error</option>
            <option value="debug" className="bg-[#0c121c]">Debug</option>
          </select>

          {/* Time Range Filter */}
          <select
            value={filters.timeRange}
            onChange={(e) => setFilters(f => ({ ...f, timeRange: e.target.value as LogFilters['timeRange'] }))}
            className="
              appearance-none bg-white/5 border border-white/10 rounded-lg
              px-3 py-1.5 pr-8 text-xs text-white/80
              hover:bg-white/8 focus:outline-none focus:ring-2 focus:ring-[#208AEC]/40
              cursor-pointer
            "
          >
            <option value="1h" className="bg-[#0c121c]">Last hour</option>
            <option value="6h" className="bg-[#0c121c]">Last 6 hours</option>
            <option value="24h" className="bg-[#0c121c]">Last 24 hours</option>
            <option value="7d" className="bg-[#0c121c]">Last 7 days</option>
            <option value="30d" className="bg-[#0c121c]">Last 30 days</option>
          </select>

          {/* Live Indicator */}
          {isLive && (
            <div className="flex items-center gap-1.5 text-xs text-emerald-400">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              Live
            </div>
          )}
        </div>

        {/* Actions */}
        {dashboardUrl && (
          <a
            href={`${dashboardUrl}/logs`}
            target="_blank"
            rel="noreferrer noopener"
            className="
              flex items-center gap-1.5 px-3 py-1.5 rounded-lg
              text-sm text-white/70 hover:text-white
              bg-white/5 hover:bg-white/10 border border-white/10
              transition-colors duration-200
            "
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15 3H21V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Open Full Logs
          </a>
        )}
      </div>

      {/* Logs List */}
      <div className="flex-1 overflow-auto font-mono text-xs">
        {filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 gap-4">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white/30">
                <rect x="2" y="3" width="22" height="20" rx="3" stroke="currentColor" strokeWidth="2"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M15 10C15 10.5523 14.5523 11 14 11H6C5.44772 11 5 10.5523 5 10C5 9.44772 5.44772 9 6 9H14C14.5523 9 15 9.44772 15 10Z" fill="currentColor"/>
              </svg>
            </div>
            <p className="text-white/40 text-sm text-center font-sans">
              {logs && logs.length > 0 
                ? 'No logs match the current filters'
                : 'No logs yet. Logs will appear here as your app runs.'
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filteredLogs.map((log) => {
              const levelStyle = LEVEL_STYLES[log.level];
              
              return (
                <div
                  key={log.id}
                  className="flex items-start gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                >
                  {/* Timestamp */}
                  <span className="text-white/40 whitespace-nowrap w-20 flex-shrink-0">
                    {formatLogTime(log.timestamp)}
                  </span>

                  {/* Level Badge */}
                  <span className={`
                    flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold
                    ${levelStyle.bg} ${levelStyle.text}
                  `}>
                    {levelStyle.label}
                  </span>

                  {/* Type */}
                  <span className="text-white/40 w-24 flex-shrink-0 truncate">
                    {log.type}
                  </span>

                  {/* Message */}
                  <span className="text-white/80 flex-1 min-w-0 break-words">
                    {log.message}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {filteredLogs.length > 0 && (
        <div className="px-4 py-2 bg-black/20 border-t border-white/5 text-xs text-white/40 font-sans">
          Showing {filteredLogs.length} of {logs?.length || 0} entries
        </div>
      )}
    </div>
  );
}

